import { useCallback, useEffect, useRef, useState } from "react";
import type { PlayerState } from "./Player";

export function usePlayer(initialMs: number | undefined, fallbackDurationMs: number): PlayerState {
  const mediaRef = useRef<HTMLVideoElement | null>(null);
  const [timeMs, setTime] = useState(initialMs ?? 0);
  const [playing, setPlaying] = useState(false);
  const [rate, setRateState] = useState(1);
  const [durationMs, setDuration] = useState(fallbackDurationMs);
  const pending = useRef<number | null>(initialMs ?? null);
  useEffect(() => {
    const el = mediaRef.current;
    if (!el) return;
    const onTime = () => {
      setTime(Math.round(el.currentTime * 1000));
    };
    const onMeta = () => {
      if (Number.isFinite(el.duration)) setDuration(Math.round(el.duration * 1000));
      if (pending.current !== null) {
        el.currentTime = pending.current / 1000;
        pending.current = null;
      }
    };
    const onPlay = () => {
      setPlaying(true);
    };
    const onPause = () => {
      setPlaying(false);
    };
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("seeked", onTime);
    el.addEventListener("loadedmetadata", onMeta);
    el.addEventListener("play", onPlay);
    el.addEventListener("pause", onPause);
    if (el.readyState >= 1) onMeta();
    return () => {
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("seeked", onTime);
      el.removeEventListener("loadedmetadata", onMeta);
      el.removeEventListener("play", onPlay);
      el.removeEventListener("pause", onPause);
    };
  });
  const seek = useCallback((ms: number, play = false) => {
    const target = Math.max(0, ms);
    setTime(target);
    const el = mediaRef.current;
    if (!el) return;
    if (el.readyState >= 1) el.currentTime = target / 1000;
    else pending.current = target;
    if (play) void el.play().catch(() => undefined);
  }, []);
  const toggle = useCallback(() => {
    const el = mediaRef.current;
    if (!el) return;
    if (el.paused) void el.play().catch(() => undefined);
    else el.pause();
  }, []);
  const setRate = useCallback((r: number) => {
    setRateState(r);
    if (mediaRef.current) mediaRef.current.playbackRate = r;
  }, []);
  return { mediaRef, timeMs, playing, rate, durationMs, seek, toggle, setRate };
}
